import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { showNotification } from 'actions/notification';
import {
  useGetKBArticleVersions,
  invalidateKBArticleVersions,
  usePostKBArticle,
  usePatchKBArticle,
} from 'actions/chatbot';
import StatusBadge from 'screens/chatbot/components/status-badge';
import PublishButton from 'screens/chatbot/components/publish-button';
import VersionsPanel from 'screens/chatbot/components/versions-panel';
import { UnpublishButton, RestoreButton } from 'screens/chatbot/components/state-buttons';

import {
  FormContainer,
  FormHeader,
  FormTitle,
  Field,
  Label,
  Input,
  TextArea,
  ButtonRow,
  SaveButton,
  DeleteButton,
  SecondaryButtonRow,
  TagInputContainer,
  TagChip,
  TagRemoveButton,
  TagTextInput,
  ModalOverlay,
  ModalBox,
  ModalTitle,
  ModalText,
  ModalActions,
  ModalCancelButton,
  ModalConfirmDeleteButton,
} from './styled-comps';

const EDITABLE_FIELDS = ['title', 'content', 'scope'];

function parseTags(scopeStr) {
  if (!scopeStr) return [];
  return scopeStr
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function EditForm({ article, onSaved, onSelectVersion, botConfig }) {
  const dispatch = useDispatch();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const tagInputRef = React.useRef(null);
  const isNew = !article.id;

  const { get: getVersions, data: versionsData } = useGetKBArticleVersions();

  const handleSaveComplete = errorMessage => {
    setSaving(false);
    if (errorMessage) {
      dispatch(
        showNotification({
          text: `Failed: ${errorMessage}`,
          duration: 5,
          showCloseIcon: true,
        }),
      );
      return;
    }
    if (article && article.article_key) {
      invalidateKBArticleVersions(article.article_key);
    }
    onSaved && onSaved();
  };

  const { post: postArticle } = usePostKBArticle({ onComplete: handleSaveComplete });
  const { patch: patchArticle } = usePatchKBArticle({ onComplete: handleSaveComplete });

  const { post: postForRestore } = usePostKBArticle({
    onComplete: (errorMessage, data) => {
      if (errorMessage || !data || !data.id) {
        setSaving(false);
        dispatch(
          showNotification({
            text: `Restore failed: ${errorMessage || 'unknown error'}`,
            duration: 5,
            showCloseIcon: true,
          }),
        );
        return;
      }
      patchArticle(data.id, { status: 'PUBLISHED' });
    },
  });

  const articleKey = article && article.article_key;
  useEffect(() => {
    if (articleKey && !isNew) {
      getVersions(articleKey);
    }
  }, [articleKey, isNew, getVersions]);

  useEffect(() => {
    setForm({
      title: article.title || '',
      scope: article.scope || '',
      content: article.content || '',
    });
    setTagInput('');
  }, [article]);

  const tags = parseTags(form.scope);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const addTag = raw => {
    const tag = raw.trim().toLowerCase();
    if (!tag) return;
    if (tags.includes(tag)) return;
    const next = [...tags, tag].join(',');
    handleChange('scope', next);
  };

  const removeTag = index => {
    const next = tags.filter((_, i) => i !== index).join(',');
    handleChange('scope', next);
  };

  const handleTagKeyDown = e => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
      setTagInput('');
    } else if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  const handleTagInputChange = e => {
    const val = e.target.value;
    if (val.includes(',')) {
      const parts = val.split(',');
      parts.forEach(p => addTag(p));
      setTagInput('');
    } else {
      setTagInput(val);
    }
  };

  const status = article && article.status;
  const isDraft = status === 'DRAFT';
  const isPublished = status === 'PUBLISHED';
  const isArchived = status === 'ARCHIVED';
  const readOnly = !isNew && !isDraft;

  const fieldPayload = () => {
    const payload = {};
    EDITABLE_FIELDS.forEach(key => {
      if (form[key] !== undefined) payload[key] = form[key];
    });
    return payload;
  };

  const handleSaveDraft = () => {
    setSaving(true);
    if (isNew) {
      postArticle({ ...fieldPayload(), bot_config: botConfig });
    } else {
      patchArticle(article.id, fieldPayload());
    }
  };

  const handleEditPublished = () => {
    setSaving(true);
    postArticle({ source_id: article.id });
  };

  const handlePublish = () => {
    setSaving(true);
    patchArticle(article.id, { status: 'PUBLISHED' });
  };

  const handleUnpublish = () => {
    setSaving(true);
    patchArticle(article.id, { status: 'ARCHIVED' });
  };

  const handleRestore = () => {
    setSaving(true);
    postForRestore({ source_id: article.id });
  };

  const handleDelete = () => {
    setShowDeleteConfirm(false);
    setSaving(true);
    patchArticle(article.id, { is_deleted: true });
  };

  const handleSelectVersionLocal = version => {
    if (onSelectVersion) onSelectVersion(version);
  };

  return (
    <FormContainer>
      <FormHeader>
        <FormTitle>{isNew ? 'New Article' : article.title}</FormTitle>
        {!isNew && <StatusBadge status={status} />}
      </FormHeader>

      <Field>
        <Label>Title</Label>
        <Input
          value={form.title || ''}
          onChange={e => handleChange('title', e.target.value)}
          disabled={readOnly}
        />
      </Field>

      <Field>
        <Label>Scope</Label>
        <TagInputContainer onClick={() => !readOnly && tagInputRef.current?.focus()}>
          {tags.map((tag, i) => (
            <TagChip key={tag}>
              {tag}
              {!readOnly && (
                <TagRemoveButton
                  onClick={e => {
                    e.stopPropagation();
                    removeTag(i);
                  }}
                >
                  &times;
                </TagRemoveButton>
              )}
            </TagChip>
          ))}
          {!readOnly && (
            <TagTextInput
              ref={tagInputRef}
              value={tagInput}
              onChange={handleTagInputChange}
              onKeyDown={handleTagKeyDown}
              placeholder={tags.length === 0 ? 'Type a scope tag and press Enter' : ''}
            />
          )}
        </TagInputContainer>
      </Field>

      <Field>
        <Label>Content</Label>
        <TextArea
          rows={20}
          value={form.content || ''}
          onChange={e => handleChange('content', e.target.value)}
          disabled={readOnly}
        />
      </Field>

      <ButtonRow>
        {(isNew || isDraft) && (
          <SaveButton
            onClick={handleSaveDraft}
            disabled={saving}
            label={saving ? 'Saving...' : isNew ? 'Save as Draft' : 'Save Draft'}
          />
        )}
        {isDraft && !isNew && (
          <PublishButton onPublish={handlePublish} disabled={saving} />
        )}
        {isPublished && (
          <>
            <SaveButton
              onClick={handleEditPublished}
              disabled={saving}
              label={saving ? 'Creating draft...' : 'Edit (creates draft)'}
            />
            <UnpublishButton
              onUnpublish={handleUnpublish}
              disabled={saving}
              noun="article"
            />
          </>
        )}
        {isArchived && <RestoreButton onRestore={handleRestore} disabled={saving} />}
      </ButtonRow>

      {!isNew && isDraft && (
        <SecondaryButtonRow>
          <DeleteButton
            onClick={() => setShowDeleteConfirm(true)}
            disabled={saving}
            label="Delete"
          />
        </SecondaryButtonRow>
      )}

      {!isNew && (
        <VersionsPanel
          versions={versionsData}
          selectedId={article.id}
          onSelect={handleSelectVersionLocal}
        />
      )}

      {showDeleteConfirm && (
        <ModalOverlay onClick={() => setShowDeleteConfirm(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>
              Are you sure you want to delete {article.title || 'this article'}?
            </ModalTitle>
            <ModalText>This action cannot be undone.</ModalText>
            <ModalActions>
              <ModalCancelButton
                label="Cancel"
                onClick={() => setShowDeleteConfirm(false)}
              />
              <ModalConfirmDeleteButton label="Delete" onClick={handleDelete} />
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </FormContainer>
  );
}

export default EditForm;
